/**
 * Utility functions for ID lookup and matching across the application
 * Supports our standardized ID system (UUID, url_id, slug)
 */

/**
 * Find an item in a collection by any of its identifiers
 *
 * @param {Array} collection - The collection to search in
 * @param {string} value - The value to match against
 * @param {Array<string>} fields - The fields to check for matches (default: ['url_id', 'slug'])
 * @returns {Object|undefined} - The found item or undefined
 */
export function findByAnyId<T extends Record<string, any>>(
  collection: T[],
  value: string,
  fields: string[] = ['url_id', 'slug']
): T | undefined {
  if (!collection || !value) return undefined;
  return collection.find(item => fields.some(field => (item as Record<string, any>)[field] === value));
}

/**
 * Find an item in a collection by any of its identifiers, with support for UUID
 * This is a more comprehensive version that includes 'id' (UUID) in the search
 *
 * @param {Array} collection - The collection to search in
 * @param {string} value - The value to match against
 * @returns {Object|undefined} - The found item or undefined
 */
export function findByAllIds<T extends Record<string, any>>(collection: T[], value: string): T | undefined {
  return findByAnyId(collection, value, ['id', 'url_id', 'slug']);
}
